import { JOURNAL_SEARCH_ERROR } from '@/entities/journal/lib/journalMessages';
import { ROOT_URL_HOST } from '@/shared/constants/contactConstants';
import { ROOT_JOURNAL_URL } from '@/shared/constants/urlConstants';
import {
  JOURNAL_PAGE_TITLE,
  JOURNAL_PAGE_DESCRIPTION,
} from '@/shared/constants/seoConstants';
import { debounce } from '@/shared/utils/debounce';
import {
  buildBreadcrumbData,
  type BreadcrumbSchemaData,
} from '@/shared/utils/buildSchemaData';
import { getErrorStatus } from '@/shared/utils/getErrorStatus';
import { buildSortOptions } from '@/shared/ui/AppSort/lib/buildSortOptions';

import { useJournalTopics } from '@/entities/journal/model/useJournalTopics';
import { buildJournalTopicMeta } from '@/pages/journal/lib/journalTopicsMeta';
import {
  JOURNAL_SORT_OPTIONS,
  JOURNAL_SORT_POPULAR,
  SEARCH_MIN_LENGTH,
  SEARCH_DEBOUNCE_DELAY,
} from '@/pages/journal/lib/useJournalConstants';

import type {
  IJournalArticle,
  IJournalTopicSection,
} from '@/entities/journal/model/types';
import type { Breadcrumb } from '@/shared/ui/AppBreadcrumbs/model/types/appBreadcrumbs';
import type { SortOption } from '@/shared/ui/AppSort/model/types/appSort';
import type { Tab } from '@/shared/ui/AppTabs/model/types/appTabs';
import type {
  JournalPayload,
  JournalSearchState,
} from '@/pages/journal/model/types/useJournal';
import { useErrorHandler } from '@/shared/lib/errors';

const useJournal = () => {
  const { handleError } = useErrorHandler();
  const route = useRoute();
  const isMobile: boolean = useIsMobile();

  const { $journalService } = useNuxtApp();

  const isDetailPage: ComputedRef<boolean> = computed(() => Boolean(route.params.slug));
  const topicSlug: ComputedRef<string> = computed(() => String(route.params.slug || ''));

  const { journalTopics, journalTopicsDropdownItems } = useJournalTopics(topicSlug);
  const isSortByPopular: ComputedRef<boolean> = computed(() => Boolean(route.query.sort));
  const url: ComputedRef<string> = computed(() => `${ROOT_URL_HOST}${route.path}`);

  const searchValue: Ref<string> = ref('');
  const selectedTab: Ref<string | number> = ref('topics');
  const searchState: Ref<JournalSearchState> = ref({
    topics: [],
    articles: [],
    articlesTotal: 0,
  });
  let searchRequestToken = 0;

  const getArticles = async (): Promise<JournalPayload> => {
    const sortParams = isSortByPopular.value
      ? { 'sort-view_count': 'desc' }
      : { 'sort-published_at': 'desc' };

    const baseParams = {
      page: 0,
      per_page: 0,
      ...(topicSlug.value ? { topic_slug: topicSlug.value } : {}),
      ...sortParams,
    };

    try {
      if (isDetailPage.value) {
        const response = await $journalService.getArticles<IJournalArticle[]>({
          ...baseParams,
          by_group: 0,
        });
        return { sections: [], articles: response.data ?? [] };
      }

      const response = await $journalService.getArticles<IJournalTopicSection[]>({
        ...baseParams,
        by_group: 1,
      });
      const sections = (response.data ?? []).filter(
        (section) => section.journals?.length,
      );
      return { sections, articles: [] };
    } catch (error) {
      logError(normalizeError(error), { action: 'journal-page' });
      throw createError({ statusCode: getErrorStatus(error), fatal: true });
    }
  };

  const pageAsyncData = useAsyncData<JournalPayload>(
    `journal-${route.fullPath}`,
    getArticles,
    {
      watch: [() => route.fullPath],
    },
  );

  const { data: journalData, status, error } = pageAsyncData;

  watchEffect(() => {
    if (error.value) {
      showError(createError({ statusCode: getErrorStatus(error.value), fatal: true }));
    }
  });

  const sections: ComputedRef<IJournalTopicSection[]> = computed(
    () => journalData.value?.sections ?? [],
  );

  const articles: ComputedRef<IJournalArticle[]> = computed(
    () => journalData.value?.articles ?? [],
  );
  const isShowSkeleton: ComputedRef<boolean> = computed(() => status.value === 'pending');

  const currentTopic = computed(() =>
    journalTopics.value.find((topic) => topic.slug === topicSlug.value),
  );

  const topicName: ComputedRef<string> = computed(() => {
    if (!isDetailPage.value) return 'Журнал';
    return articles.value[0]?.topic?.name ?? currentTopic.value?.name ?? '';
  });

  const topicDescription: ComputedRef<string> = computed(() => {
    if (!isDetailPage.value) return '';
    return articles.value[0]?.topic?.description ?? currentTopic.value?.description ?? '';
  });

  const topicsSearchResults: ComputedRef<IJournalTopicSection[]> = computed(
    () => searchState.value.topics,
  );
  const articlesSearchResults: ComputedRef<IJournalArticle[]> = computed(
    () => searchState.value.articles,
  );
  const foundResultsNumber: ComputedRef<number> = computed(() =>
    isDetailPage.value
      ? searchState.value.articlesTotal
      : topicsSearchResults.value.length + articlesSearchResults.value.length,
  );

  const tabList: ComputedRef<Tab[]> = computed(() => [
    {
      id: 1,
      label: 'Рубрики',
      value: 'topics',
      amount: topicsSearchResults.value.length || 0,
    },
    {
      id: 2,
      label: 'Статьи',
      value: 'articles',
      amount: articlesSearchResults.value.length || 0,
    },
  ]);

  const searchHandler = async (querySearch: string): Promise<void> => {
    searchRequestToken += 1;
    const requestToken = searchRequestToken;

    try {
      if (!isDetailPage.value) {
        const response = await $journalService.searchJournalTopics({
          query: querySearch,
        });
        if (requestToken !== searchRequestToken) return;
        const result = response.data ?? {};
        searchState.value = {
          topics: result.topics?.data ?? [],
          articles: result.journals?.data ?? [],
          articlesTotal: result.journals?.data?.length ?? 0,
        };
        selectedTab.value = searchState.value.topics.length ? 'topics' : 'articles';
        return;
      }

      const response = await $journalService.getArticles<IJournalArticle[]>({
        text: querySearch,
        topic_slug: topicSlug.value,
      });
      if (requestToken !== searchRequestToken) return;
      const foundArticles = response.data ?? [];
      searchState.value = {
        topics: [],
        articles: foundArticles,
        articlesTotal: response.meta?.total ?? foundArticles.length,
      };
    } catch (error) {
      handleError(error, { context: { action: JOURNAL_SEARCH_ERROR } });
    }
  };

  const debouncedSearch = debounce((querySearch: string) => {
    searchValue.value = querySearch;

    if (querySearch && querySearch.length >= SEARCH_MIN_LENGTH) {
      searchHandler(querySearch);
      return;
    }

    searchRequestToken += 1;
    searchState.value = { topics: [], articles: [], articlesTotal: 0 };
  }, SEARCH_DEBOUNCE_DELAY);

  const clearHandler = (): void => {
    debouncedSearch.cancel();
    searchRequestToken += 1;
    searchValue.value = '';
    searchState.value = { topics: [], articles: [], articlesTotal: 0 };
  };

  onScopeDispose(() => debouncedSearch.cancel());

  const sortOptions: ComputedRef<SortOption[]> = computed(() =>
    buildSortOptions(route.path, route.query, JOURNAL_SORT_OPTIONS),
  );
  const currentSort: ComputedRef<string> = computed(() =>
    isSortByPopular.value ? JOURNAL_SORT_POPULAR : 'default',
  );

  const breadcrumbs: ComputedRef<Breadcrumb[]> = computed(() => {
    if (!isDetailPage.value) {
      return [
        { id: 1, text: 'Главная', to: '/' },
        { id: 2, text: 'Журнал' },
      ];
    }

    if (!topicName.value) return [];

    return [
      { id: 1, text: 'Главная', to: '/' },
      { id: 2, text: 'Журнал', to: ROOT_JOURNAL_URL },
      {
        id: 3,
        text: topicName.value,
        to: `${ROOT_JOURNAL_URL}${topicSlug.value}/`,
        dropdownItems: journalTopicsDropdownItems.value,
        dropdownTitle: 'Выберите рубрику',
      },
    ];
  });

  const pageMeta = computed(() => {
    if (!isDetailPage.value) {
      return { title: JOURNAL_PAGE_TITLE, description: JOURNAL_PAGE_DESCRIPTION };
    }
    return buildJournalTopicMeta(
      topicSlug.value,
      topicName.value,
      topicDescription.value,
    );
  });

  const breadcrumbSchemaData: ComputedRef<BreadcrumbSchemaData> = computed(() => {
    const items = [
      { name: 'Главная', url: '/' },
      { name: 'Журнал', url: ROOT_JOURNAL_URL },
    ];
    if (isDetailPage.value && topicName.value) {
      items.push({
        name: topicName.value,
        url: `${ROOT_JOURNAL_URL}${topicSlug.value}/`,
      });
    }
    return buildBreadcrumbData(items);
  });

  const webPageSchemaData: ComputedRef<Record<string, unknown>> = computed(() => ({
    name: pageMeta.value.title,
    description: pageMeta.value.description,
    url: url.value,
  }));

  useSeoMeta({
    title: () => pageMeta.value.title,
    description: () => pageMeta.value.description,
    ogTitle: () => pageMeta.value.title,
    ogDescription: () => pageMeta.value.description,
    ogSiteName: 'Сервис по подбору юристов, адвокатов - Юрик.ру',
    ogType: 'website',
    ogUrl: () => url.value,
    ogLocale: 'ru',
    ogImage: `${ROOT_URL_HOST}/og-image.png`,
  });

  useHead({
    meta: [{ property: 'vk:image', content: `${ROOT_URL_HOST}/og-image.png` }],
  });

  return {
    pageReady: pageAsyncData,
    pageError: error,
    isMobile,
    isDetailPage,
    isShowSkeleton,
    sections,
    articles,
    topicName,
    topicDescription,
    breadcrumbs,
    breadcrumbSchemaData,
    webPageSchemaData,
    searchValue,
    selectedTab,
    tabList,
    topicsSearchResults,
    articlesSearchResults,
    foundResultsNumber,
    debouncedSearch,
    clearHandler,
    sortOptions,
    currentSort,
  };
};

export default useJournal;
